import { ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AiPortfolio } from '../../../types';
import { fetchAiPortfolios } from '../../../data/ai-portfolios';
import BottomSheet from '../../ui/bottom-sheet';
import AiPortfolioDetail from './aiPortfolioDetail';

// Shareable query key: /ai-portfolio?project=<slug> deep-links to an open case study.
const PROJECT_PARAM = 'project';

const AiPortfolioSection = () => {
  const [projects, setProjects] = useState<AiPortfolio[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    let active = true;
    fetchAiPortfolios().then((data) => {
      if (active) setProjects(data);
    });
    return () => {
      active = false;
    };
  }, []);

  // The URL is the source of truth for which sheet is open, so a shared link
  // (or the browser back/forward buttons) opens/closes the right project.
  const activeSlug = searchParams.get(PROJECT_PARAM);
  const selected = projects.find((p) => p.slug === activeSlug) ?? null;

  const handleOpen = (project: AiPortfolio) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(PROJECT_PARAM, project.slug);
        return next;
      },
      { replace: false },
    );
  };

  const handleClose = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(PROJECT_PARAM);
        return next;
      },
      { replace: false },
    );
  };

  return (
    <div className="w-full mt-4 md:mt-8" id="ai-portfolio">
      <div className="flex flex-col sm:flex-row py-4 justify-between gap-4 sm:gap-0">
        <div className="flex flex-col gap-2">
          <div className="text-sm text-brand font-medium">Showcasing</div>
          <div className="text-xl sm:text-2xl font-medium">AI Portfolio</div>
        </div>
        <Link
          to="/ui-portfolio"
          className="flex gap-2 items-center h-fit self-start sm:self-end text-sm font-medium text-blackSecondary hover:text-brand brand-transition"
        >
          <p>Looking for my UI/UX design work? View portfolio</p>
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {projects.length > 0 && (
        <div className="flex flex-col gap-4 md:gap-6 pt-2 md:pt-4">
          {projects.map((project, index) => (
            <motion.button
              key={project.slug}
              onClick={() => handleOpen(project)}
              className="group text-left w-full flex flex-col sm:flex-row gap-5 sm:gap-6 p-4 sm:p-5 rounded-2xl md:rounded-3xl border border-gray-100 bg-white hover:shadow-sm brand-transition"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{
                duration: 0.5,
                delay: index * 0.1,
                ease: 'easeOut',
              }}
            >
              <div className="w-full flex flex-col gap-3 py-1">
                {project.status && (
                  <span className="flex items-center gap-1.5 w-fit text-xs font-medium text-green-700 bg-green-50 px-2.5 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    {project.status}
                  </span>
                )}
                <div className="text-lg sm:text-xl font-medium">
                  {project.title}
                </div>
                {project.tagline && (
                  <div className="text-sm text-blackSecondary leading-relaxed">
                    {project.tagline}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-1 text-sm font-medium text-brand mt-1 group-hover:gap-2 brand-transition">
                  Read the case study
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
              {project.image && (
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 sm:w-48 sm:h-48 md:w-52 md:h-52 object-cover rounded-xl md:rounded-2xl flex-none"
                />
              )}
            </motion.button>
          ))}
        </div>
      )}

      <BottomSheet isOpen={!!selected} onClose={handleClose}>
        {selected && <AiPortfolioDetail project={selected} />}
      </BottomSheet>
    </div>
  );
};

export default AiPortfolioSection;
